import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { Camera, AlertCircle, UserCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import QrScanner from 'qr-scanner';
import IncompleteOrderModal from '../../components/IncompleteOrderModal/IncompleteOrderModal';
import axios from 'axios';
import { useBranding } from '../../contexts/BrandingContext';
import { markActivity, clearAuth } from '../../utils/session';
import { useTranslation } from 'react-i18next';

// Container with background image, blur, and overlay
const Container = styled.div`
  height: 100vh;
  display: flex;
  position: relative;
  overflow: hidden;
  animation: fadeIn 0.6s ease-out;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: url('/glasses-background.jpg.jpeg');
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    background-attachment: fixed;
    filter: blur(12px);
    transform: scale(1.05);
    z-index: 0;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.25);
    z-index: 0;
  }

  @media (max-width: 768px) {
    &::before {
      background-attachment: scroll;
    }
  }
`;

// OPTIVA Logo in top-left corner
const LogoMark = styled.div`
  position: fixed;
  top: 30px;
  left: 36px;
  font-size: 30px;
  font-weight: 600;
  color: rgba(255, 255, 255, 1);
  opacity: 1;
  text-transform: uppercase;
  letter-spacing: 0.16em;
  text-shadow: 0 2px 18px rgba(0, 0, 0, 0.45);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  filter: brightness(1.05);
  z-index: 100;
  background: rgba(255, 255, 255, 0.10);
  border: 1px solid rgba(255, 255, 255, 0.18);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: 999px;
  padding: 10px 16px;
  display: inline-block;
  pointer-events: none;

  @media (max-width: 1024px) {
    top: 28px;
    left: 32px;
    font-size: 28px;
    padding: 10px 16px;
  }

  @media (max-width: 480px) {
    top: calc(env(safe-area-inset-top, 0px) + 14px);
    left: 14px;
    font-size: 16px;
    letter-spacing: 0.14em;
    padding: 5px 9px;
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.12);
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
    text-shadow: 0 1px 8px rgba(0, 0, 0, 0.3);
    opacity: 0.85;
  }
`;

// Split-screen layout
const LeftPanel = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4rem 3rem;
  position: relative;
  z-index: 1;

  @media (max-width: 1024px) {
    display: none;
  }
`;

const LeftContent = styled.div`
  max-width: 500px;
  color: white;
  text-shadow: 0 2px 12px rgba(0, 0, 0, 0.4);
`;

const WelcomeTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: 700;
  margin: 0 0 1rem 0;
  line-height: 1.2;
  color: rgba(255, 255, 255, 1);
  text-shadow: 0 2px 20px rgba(0, 0, 0, 0.5);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  letter-spacing: -0.02em;

  @media (max-width: 1280px) {
    font-size: 3rem;
  }
`;

const WelcomeSubtitle = styled.p`
  font-size: 1.25rem;
  margin: 0 0 1.5rem 0;
  color: rgba(255, 255, 255, 0.95);
  line-height: 1.6;
  text-shadow: 0 1px 8px rgba(0, 0, 0, 0.4);
  font-weight: 400;
`;

const WelcomeSupportText = styled.p`
  font-size: 1rem;
  margin: 0;
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.7;
  text-shadow: 0 1px 6px rgba(0, 0, 0, 0.3);
`;

const RightPanel = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  position: relative;
  z-index: 1;

  @media (max-width: 1024px) {
    flex: 1;
    padding: 1.5rem;
  }
`;

const LoginCard = styled.div`
  width: 100%;
  max-width: 480px;
  background: rgba(255, 255, 255, 0.16);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  border: 1.5px solid rgba(255, 255, 255, 0.32);
  border-radius: 24px;
  padding: 2.5rem 3rem;
  box-shadow:
    0 16px 64px rgba(0, 0, 0, 0.3),
    0 8px 32px rgba(0, 0, 0, 0.2),
    0 0 0 1px rgba(255, 255, 255, 0.1),
    inset 0 1px 1px rgba(255, 255, 255, 0.4),
    inset 0 -1px 1px rgba(255, 255, 255, 0.1);
  position: relative;
  animation: fadeInUp 0.8s ease-out 0.3s both;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 24px;
    padding: 1.5px;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.8) 0%, rgba(255, 255, 255, 0.3) 100%);
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
    opacity: 0.9;
  }

  &::after {
    content: '';
    position: absolute;
    top: -1px;
    left: -1px;
    right: -1px;
    bottom: -1px;
    border-radius: 24px;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0.05) 100%);
    z-index: -1;
    opacity: 0.75;
    filter: blur(10px);
  }

  @media (max-width: 640px) {
    background: rgba(255, 255, 255, 0.22);
    border: 2px solid rgba(255, 255, 255, 0.42);
    padding: 2rem 1.5rem;
    box-shadow:
      0 12px 48px rgba(0, 0, 0, 0.35),
      0 6px 24px rgba(0, 0, 0, 0.25),
      0 0 0 1px rgba(255, 255, 255, 0.15),
      inset 0 1px 1px rgba(255, 255, 255, 0.5),
      inset 0 -1px 1px rgba(255, 255, 255, 0.2);
    &::before { opacity: 0.95; }
    &::after { opacity: 0.85; filter: blur(12px); }
  }
`;

const CardTitle = styled.h2`
  font-size: 1.75rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 1);
  margin: 0 0 0.5rem 0;
  text-align: center;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  letter-spacing: -0.01em;
`;

const CardSubtitle = styled.p`
  font-size: 0.9375rem;
  color: rgba(255, 255, 255, 0.85);
  margin: 0 0 2rem 0;
  text-align: center;
  font-weight: 400;
`;

const ScannerContainer = styled.div`
  position: relative;
  width: 100%;
  min-height: 280px;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  overflow: hidden;
  cursor: ${props => props.state === 'idle' && !props.disabled ? 'pointer' : 'default'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: ${props => {
    if (props.state === 'success') return 'linear-gradient(135deg, rgba(22, 163, 74, 0.12) 0%, rgba(16, 185, 129, 0.12) 100%)';
    if (props.state === 'scanning') return 'linear-gradient(135deg, rgba(37, 99, 235, 0.1) 0%, rgba(124, 58, 237, 0.1) 100%)';
    return 'rgba(255, 255, 255, 0.18)';
  }};
  border: ${props => {
    if (props.state === 'success') return '2.5px solid rgba(22, 163, 74, 0.35)';
    if (props.state === 'scanning') return '2.5px solid rgba(37, 99, 235, 0.4)';
    return '2.5px dashed rgba(255, 255, 255, 0.4)';
  }};
  backdrop-filter: blur(12px);
  box-shadow:
    inset 0 1px 2px rgba(255, 255, 255, 0.3),
    inset 0 -1px 1px rgba(255, 255, 255, 0.1),
    0 2px 8px rgba(0, 0, 0, 0.1);

  ${props => props.state === 'idle' && !props.disabled && `
    &:hover {
      background: rgba(255, 255, 255, 0.22);
      border-color: rgba(255, 255, 255, 0.5);
      transform: translateY(-2px);
      box-shadow:
        inset 0 1px 2px rgba(255, 255, 255, 0.4),
        inset 0 -1px 1px rgba(255, 255, 255, 0.15),
        0 4px 16px rgba(0, 0, 0, 0.15),
        0 2px 8px rgba(0, 0, 0, 0.1);
    }
    &:active {
      transform: translateY(0);
      box-shadow:
        inset 0 1px 2px rgba(255, 255, 255, 0.3),
        inset 0 -1px 1px rgba(255, 255, 255, 0.1),
        0 2px 8px rgba(0, 0, 0, 0.1);
    }
  `}

  @media (max-width: 480px) {
    min-height: 240px;
    background: rgba(255, 255, 255, 0.24);
    border: 2.5px dashed rgba(255, 255, 255, 0.55);
    box-shadow:
      inset 0 1px 2px rgba(255, 255, 255, 0.4),
      inset 0 -1px 1px rgba(255, 255, 255, 0.18),
      0 3px 12px rgba(0, 0, 0, 0.15);
    &:hover {
      background: rgba(255, 255, 255, 0.28);
      transform: translateY(-3px);
      box-shadow:
        inset 0 1px 2px rgba(255, 255, 255, 0.5),
        inset 0 -1px 1px rgba(255, 255, 255, 0.2),
        0 6px 24px rgba(0, 0, 0, 0.2);
    }
    &:active {
      transform: translateY(-1px);
      box-shadow:
        inset 0 1px 2px rgba(255, 255, 255, 0.4),
        inset 0 -1px 1px rgba(255, 255, 255, 0.18),
        0 3px 12px rgba(0, 0, 0, 0.15);
    }
  }
`;

const Video = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  border-radius: 16px;
`;

const ScannerIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.95);
  margin-bottom: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  z-index: 2;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.05);
  }

  @media (max-width: 480px) {
    width: 70px;
    height: 70px;
  }
`;

const ScannerStateText = styled.p`
  font-size: 1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 1);
  margin: 0 0 0.25rem 0;
  text-align: center;
  position: relative;
  z-index: 2;
`;

const ScannerStateSubtext = styled.p`
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.8);
  margin: 0;
  text-align: center;
  position: relative;
  z-index: 2;
`;

const CameraPermissionNote = styled.p`
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.7);
  margin: 1rem 0 0 0;
  text-align: center;
  position: relative;
  z-index: 2;
`;

const FallbackLink = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.875rem;
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
  margin: 0.5rem 0 1.5rem 0;
  transition: color 0.2s ease;
  font-weight: 500;

  &:hover {
    color: rgba(255, 255, 255, 1);
  }
`;

const AdminLink = styled.a`
  display: block;
  text-align: center;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.875rem;
  text-decoration: none;
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  transition: color 0.2s ease;
  font-weight: 500;

  &:hover {
    color: rgba(255, 255, 255, 1);
  }
`;

const ErrorMessage = styled.div`
  background: rgba(239, 68, 68, 0.15);
  backdrop-filter: blur(8px);
  color: rgba(255, 255, 255, 0.95);
  padding: 0.875rem 1rem;
  border-radius: 12px;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border: 1px solid rgba(239, 68, 68, 0.3);
  font-size: 0.875rem;
`;

const SuccessMessage = styled.div`
  background: rgba(22, 163, 74, 0.15);
  backdrop-filter: blur(8px);
  color: rgba(255, 255, 255, 0.95);
  padding: 0.875rem 1rem;
  border-radius: 12px;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border: 1px solid rgba(22, 163, 74, 0.3);
  font-size: 0.875rem;
`;

// Modal for fallback employee code entry
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
  animation: fadeIn 0.2s ease-out;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 20px;
  padding: 2rem;
  width: 100%;
  max-width: 400px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: fadeInUp 0.3s ease-out;
`;

const ModalCloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #64748b;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;

  &:hover {
    background: #f1f5f9;
    color: #1e293b;
  }
`;

const ModalTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 1rem 0;
`;

const ModalInput = styled.input`
  width: 100%;
  padding: 0.875rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 1rem;
  margin-bottom: 1rem;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const ModalButton = styled.button`
  width: 100%;
  padding: 0.875rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Where each role lands after signing in. Returns null for roles with no single
// home (admin and super_admin can open every dashboard, so there is nothing to
// pick for them) — callers decide whether to fall back or stay put.
const ROLE_HOME = {
  store_manager: '/manager/dashboard',
  advisor: '/admin/dashboard',
  lab_manager: '/lab/dashboard',
  technician: '/technician/dashboard',
  sales_person: '/visitor-entry',
  inventory_manager: '/central-store/dashboard',
  operations_manager: '/central-store/dashboard',
  courier: '/courier/dashboard',
  customer_support: '/customer-support/dashboard',
  assistant: '/admin-staff/home',
  accountant: '/admin-staff/home',
  other: '/admin-staff/home',
};

const dashboardPathForRole = (role) => ROLE_HOME[role] || null;

const Login = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { refreshBranding } = useBranding();
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [scannerState, setScannerState] = useState('idle'); // 'idle', 'scanning', 'success'
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const qrScannerRef = useRef(null);
  const [incompleteOrders, setIncompleteOrders] = useState([]);
  const [showIncompleteOrdersModal, setShowIncompleteOrdersModal] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [storeInfo, setStoreInfo] = useState(null);
  const [showFallbackModal, setShowFallbackModal] = useState(false);
  const [employeeCode, setEmployeeCode] = useState('');

  const applyLoginBranding = async (user) => {
    const rawStore = user?.store || user?.storeId;
    const storeId = typeof rawStore === 'string' ? rawStore : rawStore?._id || rawStore?.id;
    if (!storeId) return;
    try { await refreshBranding(storeId); } catch (_) {}
  };

  const startCamera = async () => {
    try {
      setError('');
      setSuccess('');
      setIsLoading(true);
      setScannerState('scanning');

      if (!videoRef.current) {
        setError('Video element not ready. Please try again.');
        setIsLoading(false);
        setScannerState('idle');
        return;
      }

      if (qrScannerRef.current) {
        qrScannerRef.current.stop();
        qrScannerRef.current.destroy();
        qrScannerRef.current = null;
      }

      setIsScanning(true);

      let retries = 0;
      while (!videoRef.current && retries < 10) {
        await new Promise(resolve => setTimeout(resolve, 100));
        retries++;
      }

      if (!videoRef.current) {
        setError('Video element not available. Please refresh the page and try again.');
        setIsLoading(false);
        setIsScanning(false);
        setScannerState('idle');
        return;
      }

      videoRef.current.style.display = 'block';
      videoRef.current.style.visibility = 'visible';
      videoRef.current.style.width = '100%';
      videoRef.current.style.height = '100%';
      videoRef.current.style.objectFit = 'cover';

      await new Promise(resolve => setTimeout(resolve, 100));

      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        try {
          const testStream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: { ideal: 'environment' } }
          });
          testStream.getTracks().forEach(track => track.stop());
        } catch (permErr) {
          if (permErr.name === 'NotAllowedError' || permErr.name === 'PermissionDeniedError') {
            setError('Camera permission denied. Please allow camera access and refresh the page.');
            setIsLoading(false);
            setIsScanning(false);
            setScannerState('idle');
            return;
          }
        }
      }

      let qrScanner = new QrScanner(
        videoRef.current,
        (result) => {
          if (result && result.data) {
            setScannerState('success');
            handleQRCodeDetected(result.data);
          }
        },
        {
          returnDetailedScanResult: true,
          highlightScanRegion: true,
          highlightCodeOutline: true,
          preferredCamera: 'environment',
          maxScansPerSecond: 5,
        }
      );

      qrScannerRef.current = qrScanner;

      try {
        await qrScanner.start();
      } catch (startError) {
        console.log('Back camera failed, trying front camera...', startError);
        try {
          qrScanner.stop();
          qrScanner.destroy();
        } catch (e) {}

        qrScanner = new QrScanner(
          videoRef.current,
          (result) => {
            if (result && result.data) {
              setScannerState('success');
              handleQRCodeDetected(result.data);
            }
          },
          {
            returnDetailedScanResult: true,
            highlightScanRegion: true,
            highlightCodeOutline: true,
            preferredCamera: 'user',
            maxScansPerSecond: 5,
          }
        );

        qrScannerRef.current = qrScanner;
        await qrScanner.start();
      }

      setIsLoading(false);
    } catch (err) {
      console.error('Camera error:', err);

      let errorMessage = 'Camera access denied. Please allow camera access and try again.';

      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        errorMessage = 'Camera permission denied. Please allow camera access and refresh the page.';
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        errorMessage = 'No camera found. Please ensure your device has a camera.';
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        errorMessage = 'Camera is already in use. Please close other apps using the camera.';
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      setIsLoading(false);
      setIsScanning(false);
      setScannerState('idle');
      if (videoRef.current) {
        videoRef.current.style.display = 'none';
      }
    }
  };

  const stopCamera = () => {
    if (qrScannerRef.current) {
      qrScannerRef.current.stop();
      qrScannerRef.current.destroy();
      qrScannerRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.style.display = 'none';
      videoRef.current.style.visibility = 'hidden';
    }
    setIsScanning(false);
    setScannerState('idle');
  };

  const handleQRCodeDetected = async (qrData) => {
    setSuccess('Badge detected! Validating...');
    setIsLoading(true);
    stopCamera();

    try {
      const cleanedEmployeeId = String(qrData || '').trim();

      if (!cleanedEmployeeId) {
        setError('Invalid badge. No employee ID detected.');
        setIsLoading(false);
        setScannerState('idle');
        return;
      }

      const response = await fetch('/api/auth/badge-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ employeeId: cleanedEmployeeId }),
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem('token', data.token);
        markActivity();   // start the idle clock fresh for this sign-in
        if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken);
        localStorage.setItem('user', JSON.stringify(data.user));
        const u = data.user;
        localStorage.setItem('user_name', u.fullName || [u.firstName, u.lastName].filter(Boolean).join(' ') || u.role || 'User');
        localStorage.setItem('user_role', u.role || '');

        // Always reflect THIS user's store — clear any leftover from a previous
        // user so an unassigned user (e.g. admin) doesn't inherit a stale store.
        if (data.user.store) {
          setStoreInfo(data.user.store);
          localStorage.setItem('storeInfo', JSON.stringify(data.user.store));
        } else {
          setStoreInfo(null);
          localStorage.removeItem('storeInfo');
        }
        await applyLoginBranding(data.user);

        setSuccess('Login successful! Redirecting...');

        if (data.user.role === 'admin') {
          setError('Admin users must use email and password login at /admin/login');
          setIsLoading(false);
          setTimeout(() => navigate('/admin/login'), 2000);
          return;
        }

        try {
          const incompleteResponse = await axios.get(`/api/orders/incomplete/${data.user.id}`, {
            headers: { Authorization: `Bearer ${data.token}` }
          });

          if (incompleteResponse.data.incompleteOrders && incompleteResponse.data.incompleteOrders.length > 0) {
            setIncompleteOrders(incompleteResponse.data.incompleteOrders);
            setShowIncompleteOrdersModal(true);
          } else {
            navigateBasedOnRole(data.user.role);
          }
        } catch (err) {
          console.error('Error fetching incomplete orders:', err);
          navigateBasedOnRole(data.user.role);
        }
      } else {
        if (data.requiresEmailPassword) {
          setError('Admin users must use email and password login. Please go to /admin/login');
          setIsLoading(false);
          setTimeout(() => navigate('/admin/login'), 3000);
        } else {
          setError(data.message || 'Invalid badge. Please try again.');
          setIsLoading(false);
          setScannerState('idle');
        }
      }
    } catch (error) {
      setError('Network error. Please try again.');
      setIsLoading(false);
      setScannerState('idle');
      console.error('Badge login error:', error);
    }
  };

  const handleFallbackLogin = async () => {
    if (!employeeCode.trim()) {
      setError('Please enter an employee code');
      return;
    }

    setError('');
    setSuccess('Validating...');
    setIsLoading(true);

    try {
      const response = await fetch('/api/auth/badge-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ employeeId: employeeCode.trim() }),
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem('token', data.token);
        markActivity();   // start the idle clock fresh for this sign-in
        if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken);
        localStorage.setItem('user', JSON.stringify(data.user));
        const u = data.user;
        localStorage.setItem('user_name', u.fullName || [u.firstName, u.lastName].filter(Boolean).join(' ') || u.role || 'User');
        localStorage.setItem('user_role', u.role || '');

        // Always reflect THIS user's store; clear any leftover from a previous user.
        if (data.user.store) {
          setStoreInfo(data.user.store);
          localStorage.setItem('storeInfo', JSON.stringify(data.user.store));
        } else {
          setStoreInfo(null);
          localStorage.removeItem('storeInfo');
        }
        await applyLoginBranding(data.user);

        setSuccess('Login successful! Redirecting...');
        setShowFallbackModal(false);
        setEmployeeCode('');

        if (data.user.role === 'admin') {
          setError('Admin users must use email and password login at /admin/login');
          setIsLoading(false);
          setTimeout(() => navigate('/admin/login'), 2000);
          return;
        }

        try {
          const incompleteResponse = await axios.get(`/api/orders/incomplete/${data.user.id}`, {
            headers: { Authorization: `Bearer ${data.token}` }
          });

          if (incompleteResponse.data.incompleteOrders && incompleteResponse.data.incompleteOrders.length > 0) {
            setIncompleteOrders(incompleteResponse.data.incompleteOrders);
            setShowIncompleteOrdersModal(true);
          } else {
            navigateBasedOnRole(data.user.role);
          }
        } catch (err) {
          console.error('Error fetching incomplete orders:', err);
          navigateBasedOnRole(data.user.role);
        }
      } else {
        if (data.requiresEmailPassword) {
          setError('Admin users must use email and password login. Please go to /admin/login');
          setIsLoading(false);
          setTimeout(() => navigate('/admin/login'), 3000);
        } else {
          setError(data.message || 'Invalid employee code. Please try again.');
          setIsLoading(false);
        }
      }
    } catch (error) {
      setError('Network error. Please try again.');
      setIsLoading(false);
      console.error('Badge login error:', error);
    }
  };

  const navigateBasedOnRole = (role) => {
    setTimeout(() => {
      navigate(dashboardPathForRole(role) || '/visitor-entry');
    }, 1000);
  };

  const handleCloseIncompleteOrdersModal = () => {
    setShowIncompleteOrdersModal(false);
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    navigateBasedOnRole(user.role);
  };

  // Reaching this screen ends the session, full stop.
  //
  // Landing here means you are leaving — signing out, pressing Back off a
  // dashboard, or handing the terminal to someone else. Any of those should
  // leave the next person a clean slate rather than a live token they could
  // walk back into. Runs once on mount, before the form is usable.
  useEffect(() => {
    clearAuth();
  }, []);

  useEffect(() => {
    const storedStoreInfo = localStorage.getItem('storeInfo');
    if (storedStoreInfo) {
      try {
        setStoreInfo(JSON.parse(storedStoreInfo));
      } catch (e) {
        console.error('Error parsing stored store info:', e);
      }
    }

    return () => {
      stopCamera();
    };
  }, []);

  const getScannerContent = () => {
    if (scannerState === 'success') {
      return (
        <>
          <Video
            ref={videoRef}
            autoPlay playsInline muted
            style={{ display: 'none', width: '100%', height: '100%', objectFit: 'cover' }}
          />
          <ScannerIcon>
            <UserCheck size={40} />
          </ScannerIcon>
          <ScannerStateText>{t('login.badgeDetected')}</ScannerStateText>
          <ScannerStateSubtext>{t('login.validatingCredentials')}</ScannerStateSubtext>
        </>
      );
    }

    if (isScanning) {
      return (
        <>
          <Video
            ref={videoRef}
            autoPlay playsInline muted
            style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover' }}
          />
          <ScannerStateText>{t('login.scanning')}</ScannerStateText>
          <ScannerStateSubtext>{t('login.positionYourBadgeInFront')}</ScannerStateSubtext>
        </>
      );
    }

    return (
      <>
        <Video
          ref={videoRef}
          autoPlay playsInline muted
          style={{ display: 'none', width: '100%', height: '100%', objectFit: 'cover' }}
        />
        <ScannerIcon onClick={startCamera}>
          <Camera size={40} />
        </ScannerIcon>
        <ScannerStateText>{t('login.readyToScan')}</ScannerStateText>
        <ScannerStateSubtext>{t('login.clickTheCameraIconTo')}</ScannerStateSubtext>
        <CameraPermissionNote>{t('login.cameraPermissionRequired')}</CameraPermissionNote>
      </>
    );
  };

  return (
    <>
      <LogoMark className="font-optiva">{t('login.optiva')}</LogoMark>
      <Container>
        <LeftPanel>
          <LeftContent>
            <WelcomeTitle className="font-optiva">{t('login.welcomeToOptiva')}</WelcomeTitle>
            <WelcomeSubtitle>
              {t('login.aPowerfulAllInOne')}
            </WelcomeSubtitle>
            <WelcomeSupportText>
              {t('login.streamlineYourOpticalBusinessOperations')}
            </WelcomeSupportText>
          </LeftContent>
        </LeftPanel>

        <RightPanel>
          <LoginCard>
            <CardTitle>{t('login.employeeSignIn')}</CardTitle>
            <CardSubtitle>{t('login.scanYourBadgeToContinue')}</CardSubtitle>

            {error && (
              <ErrorMessage>
                <AlertCircle size={18} />
                {error}
              </ErrorMessage>
            )}

            {success && (
              <SuccessMessage>
                <UserCheck size={18} />
                {success}
              </SuccessMessage>
            )}

            <ScannerContainer
              state={scannerState}
              disabled={isLoading}
              onClick={scannerState === 'idle' && !isLoading ? startCamera : undefined}
            >
              {getScannerContent()}
            </ScannerContainer>

            <FallbackLink onClick={() => setShowFallbackModal(true)}>
              {t('login.enterEmployeeCode')}
            </FallbackLink>

            <AdminLink
              href="/admin/login"
              onClick={(e) => { e.preventDefault(); navigate('/admin/login'); }}
            >
              {t('login.adminLogin')}
            </AdminLink>
          </LoginCard>
        </RightPanel>
      </Container>

      {showFallbackModal && (
        <ModalOverlay onClick={() => setShowFallbackModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalCloseButton onClick={() => setShowFallbackModal(false)}>×</ModalCloseButton>
            <ModalTitle>{t('login.enterEmployeeCode2')}</ModalTitle>
            <ModalInput
              type="text"
              placeholder={t('login.employeeId')}
              value={employeeCode}
              onChange={(e) => setEmployeeCode(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleFallbackLogin();
                }
              }}
              autoFocus
            />
            <ModalButton onClick={handleFallbackLogin} disabled={isLoading}>
              {isLoading ? t('login.validating') : t('login.login')}
            </ModalButton>
          </ModalContent>
        </ModalOverlay>
      )}

      {showIncompleteOrdersModal && (
        <IncompleteOrderModal
          incompleteOrders={incompleteOrders}
          onClose={handleCloseIncompleteOrdersModal}
        />
      )}
    </>
  );
};

export default Login;
